import { lazy, Suspense, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, AlertCircle, FileSignature, Maximize2, ExternalLink } from 'lucide-react';
import type { OnboardingTaskKind } from '@hcc/types';
import { api, ApiError } from '../../lib/api';
import { useCurrentUser } from '../../hooks/useAuth';
import { Hero } from '../../components/ui/Hero';
import { EmptyState } from '../../components/ui/EmptyState';
import { Card, CardSection } from '../../components/ui/Card';
import { SkeletonList } from '../../components/ui/Skeleton';
import { Button } from '../../components/ui/Button';
import { SignaturePadModal } from '../../components/kit/SignaturePadModal';

const PdfViewer = lazy(() => import('../../components/pdf/PdfViewer'));

interface MeTask {
  task_id: string;
  title: string;
  description: string | null;
  status: 'pending' | 'awaiting_review' | 'changes_requested' | 'complete';
  onboarding_kind?: OnboardingTaskKind;
  document_id?: string;
}
interface MeView {
  tasks: MeTask[];
}
interface DocumentMeta {
  title: string;
  requires_option: boolean;
  options: { value: string; label: string }[];
  require_read: boolean;
  ack_text: string | null;
}

/**
 * Dedicated, focused page for reading and signing one document. Reached from
 * /me (mirrors the form flow) so the inductee gets the full screen for the
 * PDF instead of a cramped inline iframe. The viewer hides the native
 * thumbnail rail and fits to width; a full-screen button and an open-in-new-tab
 * link cover desktop and mobile, where embedded PDFs are unreliable.
 */
export function DocumentSign() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: user, isLoading: sessionLoading } = useCurrentUser();

  const [read, setRead] = useState(false);
  const [option, setOption] = useState('');
  const [padOpen, setPadOpen] = useState(false);
  const viewerRef = useRef<HTMLDivElement>(null);

  const me = useQuery({
    queryKey: ['me-view'],
    enabled: !!user?.person_id,
    queryFn: () => api<MeView>('/api/me'),
  });
  const task = me.data?.tasks.find((t) => t.task_id === taskId);
  const documentId = task?.document_id;

  const meta = useQuery({
    queryKey: ['onboarding-doc-meta', documentId],
    enabled: !!documentId,
    queryFn: () => api<DocumentMeta>(`/api/me/onboarding/document/${documentId}/meta`),
  });

  const sign = useMutation({
    mutationFn: (signature_png: string) =>
      api<{ ok: true }>('/api/me/onboarding/sign', {
        method: 'POST',
        body: JSON.stringify({
          task_id: task!.task_id,
          document_id: documentId,
          ...(option ? { selected_option: option } : {}),
          signature_png,
        }),
      }),
    onSuccess: () => {
      toast.success('Signed');
      qc.invalidateQueries({ queryKey: ['me-view'] });
      navigate('/me');
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not sign'),
  });

  if (sessionLoading) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-8">
        <SkeletonList rows={4} />
      </main>
    );
  }
  // No session (expired or signed out): branded login page, not a stub.
  if (!user) return <Navigate to="/login" replace />;
  if (!user.person_id) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        This page is for attendees. <Link to="/" className="underline">Home</Link>.
      </main>
    );
  }
  if (me.isLoading) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-8">
        <SkeletonList rows={4} />
      </main>
    );
  }

  const pdfUrl = `/api/me/onboarding/document/${documentId}/pdf`;
  const needsOption = meta.data?.requires_option ?? false;
  const optionReady = !needsOption || option.length > 0;
  const requireRead = meta.data?.require_read ?? true;
  const readReady = !requireRead || read;
  const locked = task?.status === 'complete' || task?.status === 'awaiting_review';

  function goFullscreen() {
    viewerRef.current?.requestFullscreen?.().catch(() => {
      // Fullscreen API unsupported (e.g. iOS Safari) - the new-tab link covers it.
      window.open(pdfUrl, '_blank', 'noopener');
    });
  }

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-4xl space-y-5 px-4 py-8 sm:px-6">
        <Link to="/me" className="inline-flex items-center gap-1 text-sm font-medium text-fg-muted hover:text-fg">
          <ArrowLeft className="h-4 w-4" />
          Back to your onboarding
        </Link>

        {!task || task.onboarding_kind !== 'document_ack' || !documentId ? (
          <EmptyState
            icon={<AlertCircle className="h-6 w-6" />}
            title="Document not found"
            description="This document isn't assigned to you."
          />
        ) : locked ? (
          <>
            <Hero eyebrow={<span>Onboarding</span>} title={task.title} description="You've already signed this document." />
            <Card>
              <CardSection>
                <Button variant="ghost" onClick={() => navigate('/me')}>Back to your onboarding</Button>
              </CardSection>
            </Card>
          </>
        ) : (
          <>
            <Hero
              eyebrow={<span>Onboarding</span>}
              title={task.title}
              description={task.description ?? 'Read the document below, then sign to confirm.'}
            />

            <Card>
              <CardSection className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium text-fg-muted">Document</span>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" iconLeft={<Maximize2 className="h-4 w-4" />} onClick={goFullscreen}>
                      Full screen
                    </Button>
                    <a
                      href={pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-token-sm px-2 py-1 text-sm text-accent hover:underline"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Open in new tab
                    </a>
                  </div>
                </div>

                {/* pdf.js renders inline (canvas), so it works on mobile and
                    in-app browsers where a native PDF iframe shows nothing. */}
                <div
                  ref={viewerRef}
                  className="h-[60vh] w-full overflow-y-auto rounded-token-sm border border-line/10 bg-surface-2 p-2 sm:h-[75vh]"
                >
                  <Suspense
                    fallback={<p className="p-6 text-center text-sm text-fg-muted">Loading document…</p>}
                  >
                    <PdfViewer url={pdfUrl} />
                  </Suspense>
                </div>
              </CardSection>
            </Card>

            <Card>
              <CardSection className="space-y-4">
                {needsOption && meta.data ? (
                  <fieldset className="space-y-1.5">
                    <legend className="text-sm font-medium text-fg-muted">Please choose one:</legend>
                    {meta.data.options.map((o) => (
                      <label key={o.value} className="flex items-center gap-2 text-sm text-fg">
                        <input
                          type="radio"
                          name="doc-opt"
                          value={o.value}
                          checked={option === o.value}
                          onChange={() => setOption(o.value)}
                        />
                        {o.label}
                      </label>
                    ))}
                  </fieldset>
                ) : null}
                {requireRead ? (
                  <label className="flex items-start gap-2 text-sm text-fg">
                    <input
                      type="checkbox"
                      checked={read}
                      onChange={(e) => setRead(e.target.checked)}
                      className="mt-0.5 shrink-0"
                    />
                    <span>{meta.data?.ack_text?.trim() || 'I have read and understood this document.'}</span>
                  </label>
                ) : null}
                <Button
                  variant="primary"
                  iconLeft={<FileSignature className="h-4 w-4" />}
                  disabled={!readReady || !optionReady}
                  onClick={() => setPadOpen(true)}
                >
                  Sign
                </Button>
              </CardSection>
            </Card>
          </>
        )}
      </div>

      <SignaturePadModal
        open={padOpen}
        title={task ? `Sign: ${task.title}` : 'Sign'}
        onClose={() => setPadOpen(false)}
        onConfirm={async (dataUrl) => {
          await sign.mutateAsync(dataUrl);
        }}
      />
    </main>
  );
}
